import React, { memo, useCallback, useState } from 'react'
import { ImageUpload } from '../ImageUpload'
import EntitySearchDropdown from './EntitySearchDropdown'
import EntityTypeSelector from './EntityTypeSelector'
import LocationDisplay from './LocationDisplay'
import { trackSpotModePanelMinimized, trackEvent } from '../../utils/analytics'
import { baseInputClasses } from '../../styles/inputStyles'
import { useSpotEntitySearch } from '../../hooks/useSpotEntitySearch'
import EntitySocialsFields from './EntitySocialsFields'
import EntityStatusPicker from './EntityStatusPicker'

/**
 * SpotModePanel - Spot mode side panel component
 * 
 * Displays the spot mode form with entity search, location, notes, and image upload.
 * Used by both HomePageMap and EntityDetailMap when spot mode is active.
 * 
 * @param {Object} props
 * @param {Function} props.t - Translation function
 * @param {boolean} props.isSpotPanelMinimized - Panel minimized state
 * @param {Function} props.setIsSpotPanelMinimized - Set panel minimized state
 * @param {Object} props.spotModeEntity - Selected entity for sighting
 * @param {string} props.entitySearchQuery - Entity search query
 * @param {Function} props.setEntitySearchQuery - Set entity search query
 * @param {Function} props.setSpotModeEntity - Set spot mode entity
 * @param {boolean} props.showSearchDropdown - Show search dropdown
 * @param {Function} props.setShowSearchDropdown - Set show search dropdown
 * @param {Array} props.entitySearchResults - Entity search results
 * @param {string} props.spotModeEntityType - Entity type for new entries
 * @param {Function} props.setSpotModeEntityType - Set entity type
 * @param {Object} props.spotModeLocation - Spot mode location {lat, lng}
 * @param {string} props.spotModeNotes - User notes
 * @param {Function} props.setSpotModeNotes - Set notes
 * @param {Array} props.spotModeImages - Array of image URLs
 * @param {Function} props.setSpotModeImages - Set images
 * @param {Function} props.handleSpotSubmit - Submit handler
 * @param {boolean} props.isSubmittingSpot - Submitting state
 * @param {boolean} props.user - Current user
 * 
 * @idempotent - Self-contained panel component
 * @atomic - Single purpose: spot mode form
 */
const SpotModePanel = memo(function SpotModePanel({
  t,
  location,
  entity,
  onEntityChange,
  entityType,
  onEntityTypeChange,
  entities = [],
  notes,
  onNotesChange,
  socials = {},
  onSocialsChange,
  images = [],
  onImagesChange,
  onSubmit,
  onCancel,
  isSubmitting,
  isMinimized,
  onMinimizeToggle,
  user,
  isAuthenticated,
  isImmersiveMode = false,
  isLocatingGPS = false,
  entityStatus = null,
  onEntityStatusChange = null
}) {
  // Internal state for entity search UI
  const [entitySearchQuery, setEntitySearchQuery] = useState(entity?.name || '')
  const [showSearchDropdown, setShowSearchDropdown] = useState(false)

  // Server-side entity search (debounced 300ms)
  const { results: filteredEntities, isSearching } = useSpotEntitySearch({ query: entitySearchQuery })
  
  const handleMinimizeToggle = () => {
    // Track panel minimization (only when minimizing, not expanding)
    if (!isMinimized) {
      trackSpotModePanelMinimized(
        !!entity,
        !!location,
        images.length
      )
    }
    
    onMinimizeToggle(!isMinimized)
  }

  return (
    <div className={isImmersiveMode
      ? 'fixed top-0 left-0 bottom-0 z-[999] w-[360px] bg-white flex flex-col border-r border-gray-200 shadow-xl'
      : 'fixed top-24 right-4 z-[999] w-[min(24rem,calc(100vw-2rem))] bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-primary/20 overflow-hidden'
    }>
      {/* Header */}
      <div className="bg-primary/5 border-b border-primary/20 px-4 py-2 flex items-center justify-between">
        <h3 className="text-base font-bold text-gray-900">{isMinimized ? '' : t('map.spotMode.whatDidYouSee')}</h3>
        {!isImmersiveMode && (
          <button
            onClick={handleMinimizeToggle}
            className="text-gray-500 hover:bg-gray-100 rounded-full p-2 transition-colors"
            aria-label={isMinimized ? "Expand" : "Minimize"}
          >
            {isMinimized ? (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Panel Content */}
      {(!isMinimized || isImmersiveMode) && (
        <div className={isImmersiveMode ? 'flex-1 overflow-y-auto p-4' : 'p-4 max-h-[calc(100vh-12rem)] overflow-y-auto'}>
          {/* Image Upload — photos first for faster mobile submission */}
          <ImageUpload
            images={images}
            setImages={(newImages) => {
              trackEvent('SpotMode', 'images_change', 'spot_panel', newImages.length, {
                image_count: newImages.length,
                previous_count: images.length
              })
              onImagesChange(newImages)
            }}
            maxImages={5}
            minImages={1}
            disabled={isSubmitting}
          />

          {/* Entity Search */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('map.truckRequired')}
            </label>
            <div className="relative">
              <input
                type="text"
                value={entity ? entity.name : entitySearchQuery}
                onChange={(e) => {
                  trackEvent('SpotMode', 'entity_search_input', 'spot_panel', null, {
                    query_length: e.target.value.length,
                    has_entity_selected: !!entity
                  })
                  setEntitySearchQuery(e.target.value)
                  onEntityChange(null)
                }}
                onFocus={() => {
                  trackEvent('SpotMode', 'entity_search_focus', 'spot_panel', null, {
                    has_location: !!location
                  })
                  setShowSearchDropdown(true)
                }}
                placeholder={t('map.spotMode.selectEntity')}
                disabled={entity && entity.id}
                className={`${baseInputClasses} ${entity && entity.id ? 'bg-gray-100 cursor-not-allowed' : 'pr-8'}`}
                autoFocus={!entity || !entity.id}
              />
              {/* Search spinner */}
              {isSearching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin pointer-events-none" />
              )}

              {/* Search Dropdown */}
              {showSearchDropdown && !entity && entitySearchQuery.length > 0 && (
                <EntitySearchDropdown
                  t={t}
                  searchQuery={entitySearchQuery}
                  searchResults={filteredEntities}
                  onSelectEntity={(selectedEntity) => {
                    trackEvent('SpotMode', 'entity_selected', 'spot_panel', null, {
                      entity_id: selectedEntity.id,
                      entity_name: selectedEntity.name,
                      is_existing: true
                    })
                    onEntityChange(selectedEntity)
                    setEntitySearchQuery(selectedEntity.name)
                  }}
                  onCreateNew={(newEntity) => {
                    trackEvent('SpotMode', 'entity_create_new', 'spot_panel', null, {
                      entity_name: newEntity.name,
                      entity_type: entityType
                    })
                    onEntityChange(newEntity)
                  }}
                  onClose={() => {
                    trackEvent('SpotMode', 'entity_search_close', 'spot_panel')
                    setShowSearchDropdown(false)
                  }}
                  spotModeEntityType={entityType}
                />
              )}
            </div>
            {entity && (
              <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>
                  {entity.name}
                  {!entity.id && <span className="text-xs ml-1">(will be added as new)</span>}
                </span>
              </p>
            )}
          </div>

          {/* Entity Type - only show when creating new entity */}
          {entity && !entity.id && (
            <EntityTypeSelector
              value={entityType}
              onChange={(type) => onEntityTypeChange(type)}
              label={t('map.vendorType.label')}
              required={true}
            />
          )}

          {/* Social links - only show when creating new entity */}
          {entity && !entity.id && (
            <EntitySocialsFields
              socials={socials}
              onChange={onSocialsChange}
            />
          )}

          {/* Location Display */}
          {isLocatingGPS && !location ? (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('map.location')}
              </label>
              <div className="bg-gray-50 p-3 rounded-md border border-gray-200 flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
                <p className="text-sm text-gray-600">{t('map.spotMode.detectingLocation')}</p>
              </div>
            </div>
          ) : !location ? (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('map.location')}
              </label>
              <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                <p className="text-sm text-gray-500">{t('map.spotMode.tapMapToSetLocation')}</p>
              </div>
            </div>
          ) : (
            <LocationDisplay
              t={t}
              location={location}
              label={t('map.location')}
            />
          )}

          {/* Entity status picker */}
          {onEntityStatusChange && (
            <EntityStatusPicker
              value={entityStatus}
              onChange={onEntityStatusChange}
            />
          )}

          {/* Submit Button */}
          <button
            onClick={() => {
              trackEvent('SpotMode', 'submit_click', 'spot_panel', null, {
                has_entity: !!entity,
                entity_is_new: entity && !entity.id,
                has_location: !!location,
                has_notes: notes.length > 0,
                image_count: images.length,
                is_authenticated: !!user
              })
              onSubmit()
            }}
            disabled={
              !entity ||
              !location ||
              images.length < 1 ||
              images.length > 5 ||
              isSubmitting
            }
            className={`w-full py-2.5 rounded-lg font-semibold text-white transition-all ${
              !entity ||
              !location ||
              images.length < 1 ||
              images.length > 5 ||
              isSubmitting
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-primary hover:bg-primary-600 shadow-lg hover:shadow-xl'
            }`}
          >
            {isSubmitting
              ? t('map.spotMode.submitting')
              : !isAuthenticated
                ? t('spotMode.signInToSpot', 'Sign in to spot')
                : t('app.submit')}
          </button>
        </div>
      )}
    </div>
  )
})

export default SpotModePanel
